import { API } from '../Auth';
import { readableJobErrorText } from 'Utils/errorText';
import { getDataSourceNumericId } from '../DataSources/datasourcesApi';
import { getSensitiveFieldsFromDiscover } from './secureDiscoverFields';

const DISCOVER_RUNS_API = 'discoverRuns';
const DATASOURCE_BASE = '/api/datasources';
const MASKING_JOBS_BASE = '/api/v1/masking/jobs';
const MASKING_JOB_GROUPS_BASE = '/api/v1/masking/job-groups';
const ENCRYPT_JOBS_BASE = '/api/v1/encrypt/jobs';
const DECRYPT_JOBS_BASE = '/api/v1/decrypt/jobs';
const MASKING_ALGORITHMS_PATH = '/api/v1/masking/algorithms';
const MASKING_ALGORITHMS_METADATA_PATH = '/api/v1/masking/algorithms/metadata';
const MASKING_ENTITY_ALGORITHM_MAP_PATH = '/api/v1/masking/entity-algorithm-map';
const MASKING_PROVIDERS_PATH = '/api/v1/masking/providers';
const PROVIDERS_PATH = '/api/v1/providers';

// Column keys with dedicated handling on read (name/type/sensitive/algorithm) or their own
// descriptor slot (FPE key/tweak). Everything else on a saved column — dateFormat, nameOrder,
// and any future flat masking param — is collected back into the field descriptor's `params`.
const RESERVED_COLUMN_KEYS = new Set(['name', 'type', 'sensitive', 'algorithm', 'key', 'tweak']);

function paramsFromInventoryColumn(column) {
  if (!column || typeof column !== 'object') return {};
  const params = {};
  for (const [k, v] of Object.entries(column)) {
    if (!RESERVED_COLUMN_KEYS.has(k) && v != null) params[k] = v;
  }
  return params;
}

function columnAlgorithmFromInventoryColumn(column) {
  if (!column) return '';
  if (typeof column.algorithm === 'string' && column.algorithm.trim()) return column.algorithm.trim();
  if (column.algorithm && typeof column.algorithm === 'object' && column.algorithm.algorithm != null) {
    return String(column.algorithm.algorithm).trim();
  }
  return '';
}

/** Mask/encrypt-eligible connections: mongodb counts (mongodb→mongodb masking); legacy s3/sftp
 * rows stay out; dynamodb is excluded (no NoSQL masking for dynamo yet — the platform rejects it
 * with ENGINE_NOT_MASKABLE — and encryption doesn't support it either). */
function secureEligibleConnections(connections) {
  return (connections || []).filter(
    (c) => !['s3', 'sftp', 'dynamodb'].includes((c.type || '').toLowerCase())
  );
}

function countJdbcEligibleSources(connections) {
  return secureEligibleConnections(connections).length;
}

/**
 * Sources whose LATEST secure job (mask / encrypt / decrypt; CANCELLED skipped) FAILED and
 * started inside the trailing window. This is the "errors" a landscape header should count:
 * a source whose later re-run succeeded is not an error anyone has to act on, and failures
 * from months ago are history, not a to-do. `sourceIds` (optional) restricts the count to the
 * census-visible sources so it reconciles with the header's totals.
 */
function countSourcesWithLatestFailure(jobs, windowDays, sourceIds) {
  const allow = sourceIds ? new Set([...sourceIds].map((id) => String(id))) : null;
  const cutoff = Date.now() - Number(windowDays || 0) * 86400000;
  const byId = new Map();
  (jobs || []).forEach((job) => {
    if (!job || job.status === 'CANCELLED') return;
    mutatedDataSourceIdsOf(job).forEach((id) => {
      if (id == null) return;
      if (allow && !allow.has(String(id))) return;
      const prev = byId.get(String(id));
      if (!prev || jobTs(job) > jobTs(prev) || (jobTs(job) === jobTs(prev) && (Number(job.id) || 0) > (Number(prev.id) || 0))) {
        byId.set(String(id), job);
      }
    });
  });
  let n = 0;
  byId.forEach((job) => {
    if (job.status !== 'FAILED') return;
    const started = job.startedAt || job.finishedAt;
    const t = started ? Date.parse(started) : NaN;
    if (!windowDays || (Number.isFinite(t) && t >= cutoff)) n += 1;
  });
  return n;
}

const jobTs = (j) => {
  const t = j && (j.finishedAt || j.startedAt);
  return t ? Date.parse(t) || 0 : 0;
};

function jobTypeIsDecrypt(job) {
  const jt = String(job && job.jobType ? job.jobType : '').toUpperCase();
  return jt.includes('DECRYPT');
}

/**
 * Human-readable one-liner for a failed job, for tooltips. The worker's `upstreamErrorBody`
 * is usually a JSON blob (e.g. a 404 with a big `hints` map); pull out its `message` and the
 * upstream HTTP status rather than dumping the whole thing. Falls back to `errorCode`
 * (e.g. WORKER_REJECTED) and then a generic line. Never returns the raw hints object.
 */
function readableJobError(job) {
  return readableJobErrorText(job);
}

/** Encrypt + decrypt jobs for one data source, newest first (same ordering as encryption sources table). */
function mergeEncryptionJobsForDataSource(encryptJobs, decryptJobs, dataSourceId) {
  // Compare as strings (C2D-1094): the row's id comes through getDataSourceNumericId as a number,
  // but job records carry dataSourceId as a string ("11"), so a strict === matched nothing on a
  // reload — the merged list came back empty and the row fell back to UNSECURED, making the decrypt
  // option (shown only when the derived status is ENCRYPTED) vanish after the page was reloaded.
  const key = String(dataSourceId);
  const e = (encryptJobs || []).filter((j) => j && String(j.dataSourceId) === key);
  const d = (decryptJobs || []).filter((j) => j && String(j.dataSourceId) === key);
  return [...e, ...d].sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
}

/** Row status from merged job list (newest-first). */
function mapEncryptionRowStatus(jobs) {
  for (const job of jobs || []) {
    const st = job.status;
    if (st === 'CANCELLED') continue;
    if (st === 'PENDING' || st === 'RUNNING') {
      return jobTypeIsDecrypt(job) ? 'DECRYPTING' : 'ENCRYPTING';
    }
    if (st === 'FAILED') return 'ERROR';
    if (st === 'SUCCEEDED') {
      return jobTypeIsDecrypt(job) ? 'UNSECURED' : 'ENCRYPTED';
    }
  }
  return 'UNSECURED';
}

/**
 * JDBC-eligible sources and how many are FPE-encrypted vs reverted via a successful decrypt job (discover-runs job APIs only).
 */
async function getEncryptionOverviewCounts(connections) {
  // mongodb stays excluded HERE: encryption jobs don't support mongo (masking does).
  // dynamodb is excluded from encryption too.
  const filtered = (connections || []).filter(
    (c) => !['s3', 'sftp', 'mongodb', 'dynamodb'].includes((c.type || '').toLowerCase())
  );
  const total = filtered.length;
  let encrypted = 0;
  let decrypted = 0;

  const [encRes, decRes] = await Promise.all([
    listEncryptJobs({ page: 0, size: 500, sort: ['id,desc'] }),
    listDecryptJobs({ page: 0, size: 500, sort: ['id,desc'] }),
  ]);
  const encryptJobs = encRes && Array.isArray(encRes.content) ? encRes.content : [];
  const decryptJobs = decRes && Array.isArray(decRes.content) ? decRes.content : [];

  filtered.forEach((conn) => {
    const dataSourceId = getDataSourceNumericId({
      id: conn.id,
      tableData: conn.id != null ? { id: conn.id } : undefined,
    });
    if (dataSourceId == null) return;
    const merged = mergeEncryptionJobsForDataSource(encryptJobs, decryptJobs, dataSourceId);
    const status = mapEncryptionRowStatus(merged);
    if (status === 'ENCRYPTED') {
      encrypted += 1;
      return;
    }
    if (
      status === 'UNSECURED' &&
      (merged || []).some((j) => j && j.status === 'SUCCEEDED' && jobTypeIsDecrypt(j))
    ) {
      decrypted += 1;
    }
  });

  return { total, encrypted, decrypted };
}

async function getSecureDataLandscape(connections) {
  const result = {
    totalSources: 0,
    encryptedCount: 0,
    decryptedCount: 0,
    maskingTotalSources: 0,
    maskedCount: 0,
  };

  result.totalSources = countJdbcEligibleSources(connections);

  try {
    const encryption = await getEncryptionOverviewCounts(connections);
    result.encryptedCount = encryption.encrypted;
    result.decryptedCount = encryption.decrypted;
  } catch (e) {
    console.log('getEncryptionOverviewCounts', e);
  }

  try {
    const masking = await getMaskingOverviewCounts(connections);
    result.maskingTotalSources = masking.total;
    result.maskedCount = masking.masked;
  } catch (e) {
    console.log('getMaskingOverviewCounts', e);
  }

  return result;
}

/** Same job-status mapping as MaskingSources; used for /secure overview counts. */
function mapMaskingOverviewStatus(status) {
  switch (status) {
    case 'PENDING':
    case 'RUNNING':
      return 'RUNNING';
    case 'SUCCEEDED':
      return 'MASKED';
    case 'FAILED':
      return 'ERROR';
    case 'CANCELLED':
      return 'UNMASKED';
    default:
      return 'UNSECURED';
  }
}

/**
 * Whole-source masking status per data source id, mirroring the backend
 * DataSourceMaskingStatusDeriver: scan each source's MASKING jobs newest-first,
 * skip CANCELLED, and take the first that is PENDING/RUNNING (MASKING),
 * FAILED (ERROR), or SUCCEEDED (MASKED). Sources with no masking job are absent.
 *
 * Keyed on the MUTATED data source: the cross-source target when `targetDataSourceId` is
 * set, else `dataSourceId` (in-place). A cross-source job only READS its source of record,
 * so it must not mark that source masked — the target is what changed. The Sensitive
 * Data Landscape pipeline unions this over the entity-roll-up statuses so masked FILE /
 * folder-search sources — which `/api/v1/landscape/entities` omits (no roll-up rows) — still
 * land in the Masked column.
 * @returns {Record<string|number, 'MASKED'|'MASKING'|'ERROR'>}
 */
/** Effective cross-source target ids: the fan-out list when present, else the singular field, else []. */
function jobTargetIdsOf(job) {
  if (Array.isArray(job?.targetDataSourceIds) && job.targetDataSourceIds.length > 0) {
    return job.targetDataSourceIds;
  }
  return job?.targetDataSourceId != null ? [job.targetDataSourceId] : [];
}

/** Every data source MUTATED by a job: all its targets (fan-out = several), else the in-place source. */
function mutatedDataSourceIdsOf(job) {
  const targets = jobTargetIdsOf(job);
  if (targets.length > 0) return targets;
  return job?.dataSourceId != null ? [job.dataSourceId] : [];
}

function deriveMaskStatusByDataSource(jobs) {
  const byId = new Map();
  (jobs || []).forEach((job) => {
    if (!job) return;
    mutatedDataSourceIdsOf(job).forEach((mutatedId) => {
      if (mutatedId == null) return;
      if (!byId.has(mutatedId)) byId.set(mutatedId, []);
      byId.get(mutatedId).push(job);
    });
  });
  const ts = (j) => {
    const t = j && (j.finishedAt || j.startedAt);
    return t ? Date.parse(t) : 0;
  };
  const out = {};
  byId.forEach((list, id) => {
    const ordered = list.slice().sort((a, b) => ts(b) - ts(a) || (Number(b.id) || 0) - (Number(a.id) || 0));
    for (const job of ordered) {
      const st = job.status;
      if (st === 'CANCELLED') continue;
      if (st === 'PENDING' || st === 'RUNNING') { out[id] = 'MASKING'; break; }
      if (st === 'FAILED') { out[id] = 'ERROR'; break; }
      if (st === 'SUCCEEDED') { out[id] = 'MASKED'; break; }
    }
  });
  return out;
}

/**
 * Mask-eligible connection count and how many have a latest masking job in SUCCEEDED (same API as masking list: listMaskingJobs).
 */
async function getMaskingOverviewCounts(connections) {
  // mongodb is mask-eligible now (mongodb→mongodb); legacy s3/sftp rows stay out.
  // dynamodb is excluded: no NoSQL masking for dynamo yet; platform rejects with
  // ENGINE_NOT_MASKABLE.
  const filtered = (connections || []).filter(
    (c) => !['s3', 'sftp', 'dynamodb'].includes((c.type || '').toLowerCase())
  );
  const total = filtered.length;
  let masked = 0;

  const jobsResponse = await listMaskingJobs({ page: 0, size: 500, sort: ['id,desc'] });
  const jobs = jobsResponse && Array.isArray(jobsResponse.content) ? jobsResponse.content : [];
  // ONE deriver with the Data Privacy Workflow (deriveMaskStatusByDataSource: keyed on the
  // MUTATED sources, newest by timestamp, CANCELLED skipped) so the two landscapes report the
  // same masked count. This used to take the newest job by id and treat CANCELLED as unmasked.
  const statusById = deriveMaskStatusByDataSource(jobs);

  filtered.forEach((conn) => {
    const dataSourceId = getDataSourceNumericId({
      id: conn.id,
      tableData: conn.id != null ? { id: conn.id } : undefined,
    });
    if (dataSourceId == null) return;
    if (statusById[dataSourceId] === 'MASKED') {
      masked += 1;
    }
  });

  return { total, masked };
}

async function getSensitiveFields(connection, connections) {
  return getSensitiveFieldsFromDiscover(connection, connections);
}

/**
 * Table/field totals and masked (sensitive) counts from a saved masking inventory.
 * Mirrors getSensitiveFieldCountFromInventory but also tracks table-level coverage.
 * @returns {{ totalTables: number, totalFields: number, maskedTables: number, maskedFields: number }}
 */
function getMaskedInventorySummary(inventory) {
  const summary = { totalTables: 0, totalFields: 0, maskedTables: 0, maskedFields: 0 };
  if (!inventory || typeof inventory !== 'object') return summary;

  const schemas = Array.isArray(inventory.schemas) ? inventory.schemas : [];
  schemas.forEach((schema) => {
    (schema.tables || []).forEach((table) => {
      summary.totalTables += 1;
      const columns = Array.isArray(table.columns) ? table.columns : [];
      summary.totalFields += columns.length;
      let tableHasMasked = false;
      columns.forEach((column) => {
        if (column && column.sensitive) {
          summary.maskedFields += 1;
          tableHasMasked = true;
        }
      });
      if (tableHasMasked) summary.maskedTables += 1;
    });
  });
  if (summary.totalTables > 0 || summary.totalFields > 0) return summary;

  // Legacy { tables: [{ tableName, columns: { field: def } }] } shape.
  (Array.isArray(inventory.tables) ? inventory.tables : []).forEach((table) => {
    const cols = table && table.columns;
    if (cols && typeof cols === 'object' && !Array.isArray(cols)) {
      summary.totalTables += 1;
      const keys = Object.keys(cols);
      summary.totalFields += keys.length;
      if (keys.length > 0) {
        summary.maskedFields += keys.length;
        summary.maskedTables += 1;
      }
    }
  });
  return summary;
}

/**
 * Group masking jobs into parent (per masking RELATIONSHIP — (source, target-set); in-place
 * = (source, none)) + child (per run) rows for a material-table parentChildData history view.
 * A fan-out job (one job, N targets) is ONE relationship — one ledger row showing every
 * destination — because it IS one job: one read+mask pass, one status, one results page.
 * Keying on the target set keeps distinct shapes distinct: prod → [dev,test,bi] and
 * prod → dev are separate rows. The masking DTO carries no name/engine — the table resolves
 * those from AppContext connections.
 */
function transformMaskingJobsToReportRows(jobs = [], pageIndex = 0) {
  const ts = (j) => {
    const t = j && (j.finishedAt || j.startedAt);
    return t ? Date.parse(t) : 0;
  };

  const byDataSource = new Map();
  (jobs || []).forEach((job) => {
    if (!job || job.dataSourceId == null) return;
    const key = `id:${job.dataSourceId}->${jobTargetIdsOf(job).join('+')}`;
    if (!byDataSource.has(key)) byDataSource.set(key, []);
    byDataSource.get(key).push(job);
  });

  const groupKeys = Array.from(byDataSource.keys());
  groupKeys.sort((a, b) => {
    const maxA = Math.max(...byDataSource.get(a).map(ts));
    const maxB = Math.max(...byDataSource.get(b).map(ts));
    return maxB - maxA;
  });

  const result = [];
  groupKeys.forEach((groupKey) => {
    const items = byDataSource.get(groupKey).slice().sort((a, b) => ts(b) - ts(a));
    const mostRecent = items[0];
    const dataSourceId = mostRecent.dataSourceId;
    const targetIds = jobTargetIdsOf(mostRecent);
    const connectionParentId = `mask_id-${dataSourceId}_t${targetIds.length ? targetIds.join('+') : 'inplace'}_p${pageIndex}`;

    result.push({
      searchid: connectionParentId,
      dataSourceId,
      jobId: mostRecent.id,
      operation: 'Masking',
      date: ts(mostRecent),
      recordsProcessed: Number(mostRecent.recordsProcessed) || 0,
      status: mapMaskingOverviewStatus(mostRecent.status),
      errorMessage: mostRecent.status === 'FAILED' ? readableJobError(mostRecent) : null,
      // Which worker service ran this job — the re-run (play) button forwards it so the new job
      // uses the same provider. inventoryId is the exact inventory the job ran with (null for
      // pre-column jobs); the eye link + re-run resolve it, falling back to the source's latest.
      registeredServiceId: mostRecent.registeredServiceId || null,
      inventoryId: mostRecent.inventoryId ?? null,
      // Cross-source destinations (empty = in-place). A fan-out job carries the full list —
      // the ledger renders "source → t1 · t2 · t3" as ONE row, since it is one job.
      targetDataSourceIds: targetIds.length ? targetIds : null,
      targetDataSourceId: targetIds.length === 1 ? targetIds[0] : null,
      // Tables/records progress for the ledger's running-row bar; null once terminal.
      progress: jobProgressFromJob(mostRecent),
      isConnectionParent: true,
    });

    for (let i = 1; i < items.length; i += 1) {
      const job = items[i];
      const childTargets = jobTargetIdsOf(job);
      result.push({
        searchid: String(job.id),
        dataSourceId: job.dataSourceId ?? dataSourceId,
        jobId: job.id,
        operation: 'Masking',
        date: ts(job),
        status: mapMaskingOverviewStatus(job.status),
        errorMessage: job.status === 'FAILED' ? readableJobError(job) : null,
        registeredServiceId: job.registeredServiceId || null,
        inventoryId: job.inventoryId ?? null,
        targetDataSourceIds: childTargets.length ? childTargets : null,
        targetDataSourceId: childTargets.length === 1 ? childTargets[0] : null,
        parent_connection: connectionParentId,
      });
    }
  });

  return result;
}

/**
 * Job Results status for an encrypt/decrypt job. Parallels mapMaskingOverviewStatus but keeps
 * direction-aware success wording: a SUCCEEDED encrypt → ENCRYPTED, a SUCCEEDED decrypt →
 * DECRYPTED. FAILED → ERROR (so the table's error tooltip fires). The report table only special-
 * cases 'ERROR' and 'MASKED' for styling; ENCRYPTED/DECRYPTED render as plain status badges.
 */
function mapEncryptionReportStatus(job) {
  switch (job && job.status) {
    case 'PENDING':
    case 'RUNNING':
      return 'RUNNING';
    case 'SUCCEEDED':
      return jobTypeIsDecrypt(job) ? 'DECRYPTED' : 'ENCRYPTED';
    case 'FAILED':
      return 'ERROR';
    case 'CANCELLED':
      return 'CANCELLED';
    default:
      return 'UNSECURED';
  }
}

/**
 * Group encrypt + decrypt jobs into parent (per data source) + child (per run) rows for the
 * Job Results table — same shape as transformMaskingJobsToReportRows so the rows interleave in
 * one table. Each row is tagged `operation` ('Encryption' | 'Decryption') for the OPERATION
 * column, and carries a readable `errorMessage`. No results link yet (encrypt/decrypt results
 * viewer is a follow-up) — `jobId` is still set so a future RESULTS link can use it.
 */
function transformEncryptionJobsToReportRows(encryptJobs = [], decryptJobs = [], pageIndex = 0) {
  const ts = (j) => {
    const t = j && (j.finishedAt || j.startedAt);
    return t ? Date.parse(t) : 0;
  };
  const opFor = (job) => (jobTypeIsDecrypt(job) ? 'Decryption' : 'Encryption');

  const all = [...(encryptJobs || []), ...(decryptJobs || [])];
  const byDataSource = new Map();
  all.forEach((job) => {
    if (!job || job.dataSourceId == null) return;
    const key = `id:${job.dataSourceId}`;
    if (!byDataSource.has(key)) byDataSource.set(key, []);
    byDataSource.get(key).push(job);
  });

  const groupKeys = Array.from(byDataSource.keys());
  groupKeys.sort((a, b) => {
    const maxA = Math.max(...byDataSource.get(a).map(ts));
    const maxB = Math.max(...byDataSource.get(b).map(ts));
    return maxB - maxA;
  });

  const result = [];
  groupKeys.forEach((groupKey) => {
    const items = byDataSource.get(groupKey).slice().sort((a, b) => ts(b) - ts(a));
    const mostRecent = items[0];
    const dataSourceId = mostRecent.dataSourceId;
    const connectionParentId = `enc_id-${dataSourceId}_p${pageIndex}`;

    result.push({
      searchid: connectionParentId,
      dataSourceId,
      jobId: mostRecent.id,
      operation: opFor(mostRecent),
      date: ts(mostRecent),
      recordsProcessed: Number(mostRecent.recordsProcessed) || 0,
      status: mapEncryptionReportStatus(mostRecent),
      errorMessage: mostRecent.status === 'FAILED' ? readableJobError(mostRecent) : null,
      registeredServiceId: mostRecent.registeredServiceId || null,
      inventoryId: mostRecent.inventoryId ?? null,
      // Tables/records progress for the ledger's running-row bar; null once terminal.
      progress: jobProgressFromJob(mostRecent),
      isConnectionParent: true,
    });

    for (let i = 1; i < items.length; i += 1) {
      const job = items[i];
      result.push({
        // Namespaced so an encrypt job id can't collide with a masking job id of the same
        // number when both transforms' rows share one table (material-table keys on searchid).
        searchid: `enc-${job.id}`,
        dataSourceId: job.dataSourceId ?? dataSourceId,
        jobId: job.id,
        operation: opFor(job),
        date: ts(job),
        status: mapEncryptionReportStatus(job),
        errorMessage: job.status === 'FAILED' ? readableJobError(job) : null,
        registeredServiceId: job.registeredServiceId || null,
        inventoryId: job.inventoryId ?? null,
        parent_connection: connectionParentId,
      });
    }
  });

  return result;
}

function selectedFieldsToInventory(selectedFields, datasource, updatedBy = 'ui-user', opts = {}) {
  const schemasMap = {};
  for (const tablePath in (selectedFields || {})) {
    // File sources: the table key IS the file name — never dot-split it. Splitting
    // "data.2024.csv" into schema "data" / table "2024.csv" produced a tableName the
    // platform normalizer refuses to re-fold (it contains a dot), so the worker's
    // files[].pattern matched nothing. Blank schema keeps the name verbatim.
    const fileSource = opts.fileSource === true;
    const tableParts = tablePath.split('.');
    const hasSchema = !fileSource && tableParts.length > 1;
    // When the discovered tablePath has no schema prefix, emit a blank schema
    // name. The platform API's MaskingInventoryNormalizer skips blank schemas
    // when flattening, so the masking service receives a bare tableName and
    // resolves the schema via connection.schema (or the engine default:
    // Postgres -> public, SQL Server -> dbo, Oracle -> username, MySQL -> none).
    // Emitting 'default' here previously produced "default"."payers" which
    // does not exist in any real database.
    const schemaName = hasSchema ? tableParts[0] : '';
    const tableName = hasSchema ? tableParts.slice(1).join('.') : tablePath;
    if (!schemasMap[schemaName]) {
      schemasMap[schemaName] = [];
    }
    const columns = Object.entries(selectedFields[tablePath] || {}).map(([fieldName, fieldData]) => {
      const col = {
        name: fieldName,
        type: (fieldData && fieldData.dataType) ? String(fieldData.dataType).toUpperCase() : 'VARCHAR',
        sensitive: Boolean(fieldData && fieldData.checked),
      };
      const algoRaw = fieldData && fieldData.algorithm;
      if (algoRaw != null) {
        if (typeof algoRaw === 'object' && algoRaw.algorithm != null) {
          col.algorithm = algoRaw;
        } else {
          const s = String(algoRaw).trim();
          if (s) col.algorithm = s;
        }
      }
      // Flat masking params (e.g. dateFormat, nameOrder) ride as siblings of `algorithm` on the
      // column — the platform's MaskingInventoryNormalizer copies every column key except
      // name/columnName through to the worker's FieldConfig verbatim. Emit each param key,
      // never clobbering a reserved column key. `params` is the single in-UI representation;
      // it flattens here only at the inventory-JSON boundary and is re-collected on read
      // (inventoryToSelectedFieldsFromSchemas).
      const params = fieldData && fieldData.params;
      if (params && typeof params === 'object' && !Array.isArray(params)) {
        for (const [pk, pv] of Object.entries(params)) {
          if (pk && pv != null && col[pk] === undefined) col[pk] = pv;
        }
      }
      // `manual`, `recommendedAlgorithm`, `recommendedParams`, and `saved` are derived UI state —
      // never persisted. The saved `algorithm`/params are the source of truth; the recommendation
      // is recomputed on load.
      return col;
    });
    // File sources: attach the discovery-derived routing metadata (sniffedType /
    // delimiter) to the table object. The platform normalizer retains exactly these
    // two keys so file masking can pick the per-file format.
    const tableMeta = fileSource && opts.tableMeta ? opts.tableMeta[tablePath] : null;
    // Any source: extra per-table keys to persist verbatim — today the target-derived
    // inventory's `sourceColumns` (the INSERT copy restriction for a target that lacks
    // some source columns; see targetComparison.tableExtrasFromReport).
    const tableExtras = opts.tableExtras ? opts.tableExtras[tablePath] : null;
    schemasMap[schemaName].push({
      name: tableName,
      columns,
      ...(tableMeta || {}),
      ...(tableExtras || {}),
    });
  }

  return {
    datasource,
    schemas: Object.entries(schemasMap).map(([schemaName, tables]) => ({
      name: schemaName,
      tables,
    })),
    updatedBy,
  };
}

function inventoryToSelectedFieldsFromSchemas(schemas) {
  const result = {};
  (Array.isArray(schemas) ? schemas : []).forEach((schema) => {
    const schemaName = schema && schema.name ? schema.name : 'default';
    (schema.tables || []).forEach((table) => {
      const tableName = table && table.name ? table.name : '';
      if (!tableName) return;
      const key = schemaName === 'default' ? tableName : `${schemaName}.${tableName}`;
      const fields = {};
      (table.columns || []).forEach((column) => {
        const fieldName = column && column.name;
        if (!fieldName) return;
        const algo = columnAlgorithmFromInventoryColumn(column);
        // key/tweak live as flat siblings on the column (current shape) or under a nested
        // `algorithm` object (legacy inventories saved before the flat-shape fix). Read both.
        const nested = column && column.algorithm && typeof column.algorithm === 'object' ? column.algorithm : null;
        const fpeKey =
          column && column.key != null ? String(column.key) : nested && nested.key != null ? String(nested.key) : '';
        const fpeTweak =
          column && column.tweak != null
            ? String(column.tweak)
            : nested && nested.tweak != null
              ? String(nested.tweak)
              : '';
        // `saved: true` — this field came back from a persisted inventory, so its `algorithm`
        // is the source of truth. `recommendedAlgorithm` is filled live by the merge from the
        // entity→algorithm map; default '' here for callers that read this directly.
        const params = paramsFromInventoryColumn(column);
        fields[fieldName] = {
          dataType: (column && column.type ? String(column.type).toLowerCase() : 'string'),
          findings: [],
          checked: Boolean(column && column.sensitive),
          algorithm: algo,
          recommendedAlgorithm: '',
          // Flat params (dateFormat / nameOrder / …) re-collected from the saved column so a
          // re-save round-trips them; the merge treats these as the saved truth for the field.
          params,
          saved: true,
          ...(fpeKey ? { fpeKey } : {}),
          ...(fpeTweak ? { fpeTweak } : {}),
        };
      });
      result[key] = fields;
    });
  });
  return result;
}

function inventoryToSelectedFieldsFromTables(tables) {
  const result = {};
  (Array.isArray(tables) ? tables : []).forEach((table) => {
    const tableName = table && (table.tableName || table.name);
    if (!tableName) return;
    const fields = {};
    const cols = table.columns;
    if (cols && typeof cols === 'object' && !Array.isArray(cols)) {
      Object.entries(cols).forEach(([fieldName, colDef]) => {
        const def = typeof colDef === 'string' ? { algorithm: colDef } : colDef;
        const algo = columnAlgorithmFromInventoryColumn(def);
        const nested = def && def.algorithm && typeof def.algorithm === 'object' ? def.algorithm : null;
        const fpeKey = nested && nested.key != null ? String(nested.key) : '';
        const fpeTweak = nested && nested.tweak != null ? String(nested.tweak) : '';
        // Legacy table shape — same as the schemas reader: came from a saved inventory, so
        // `saved: true` and the saved `algorithm` is truth. See the note above.
        const params = paramsFromInventoryColumn(def);
        fields[fieldName] = {
          dataType: (def && def.type) ? String(def.type).toLowerCase() : 'string',
          findings: [],
          checked: true,
          algorithm: algo,
          recommendedAlgorithm: '',
          params,
          saved: true,
          ...(fpeKey ? { fpeKey } : {}),
          ...(fpeTweak ? { fpeTweak } : {}),
        };
      });
    }
    if (Object.keys(fields).length > 0) {
      result[tableName] = fields;
    }
  });
  return result;
}

function inventoryToSelectedFields(inventory) {
  if (!inventory || typeof inventory !== 'object') return {};
  if (inventory.code === 'INVENTORY_NOT_FOUND') return {};
  const fromSchemas = inventoryToSelectedFieldsFromSchemas(inventory.schemas);
  if (Object.keys(fromSchemas).length > 0) return fromSchemas;
  return inventoryToSelectedFieldsFromTables(inventory.tables);
}

function isFpeColumnDef(def) {
  if (!def || typeof def !== 'object') return false;
  if (def.algorithm && typeof def.algorithm === 'object') {
    const a = String(def.algorithm.algorithm || '').toUpperCase();
    return a === 'FPE_UTF8' || a === 'FPE_EBCDIC';
  }
  const a = String(def.algorithm || '').toUpperCase();
  return a === 'FPE_UTF8' || a === 'FPE_EBCDIC';
}

function getSensitiveFieldCountFromInventory(inventory) {
  if (!inventory || typeof inventory !== 'object') return 0;
  const schemas = Array.isArray(inventory.schemas) ? inventory.schemas : [];
  let count = 0;
  schemas.forEach((schema) => {
    (schema.tables || []).forEach((table) => {
      (table.columns || []).forEach((column) => {
        if (column && column.sensitive) count++;
      });
    });
  });
  if (count > 0) return count;
  (Array.isArray(inventory.tables) ? inventory.tables : []).forEach((table) => {
    const cols = table && table.columns;
    if (cols && typeof cols === 'object' && !Array.isArray(cols)) {
      count += Object.keys(cols).length;
    }
  });
  return count;
}

const FPE_UTF8 = 'FPE_UTF8';

function selectedFieldsToEncryptInventory(selectedFields, datasource, defaultKey, updatedBy = 'ui-user') {
  const dk = defaultKey != null ? String(defaultKey).trim() : '';
  const schemasMap = {};
  for (const tablePath in (selectedFields || {})) {
    const tableParts = tablePath.split('.');
    const hasSchema = tableParts.length > 1;
    // Blank schema name (not 'default') when the tablePath is unqualified so
    // the platform normalizer leaves tableName bare and the masking service
    // resolves the schema via connection.schema / the engine default. See
    // selectedFieldsToInventory above for the full rationale.
    const schemaName = hasSchema ? tableParts[0] : '';
    const tableName = hasSchema ? tableParts.slice(1).join('.') : tablePath;
    if (!schemasMap[schemaName]) schemasMap[schemaName] = [];
    const columns = Object.entries(selectedFields[tablePath] || {}).map(([fieldName, fieldData]) => {
      const col = {
        name: fieldName,
        type: (fieldData && fieldData.dataType) ? String(fieldData.dataType).toUpperCase() : 'VARCHAR',
        sensitive: Boolean(fieldData && fieldData.checked),
      };
      if (fieldData && fieldData.checked) {
        const key =
          fieldData.fpeKey != null && String(fieldData.fpeKey).trim() !== ''
            ? String(fieldData.fpeKey).trim()
            : dk;
        const tweak =
          fieldData.fpeTweak != null && String(fieldData.fpeTweak).trim() !== ''
            ? String(fieldData.fpeTweak).trim()
            : fieldName;
        // Encryption is ALWAYS FPE — the user only picks which columns to encrypt, never an
        // algorithm. Emit the FLAT worker shape the masking service expects: `algorithm` is a
        // string, with `mode`/`tweak`/`key` as sibling properties (NOT a nested object — the
        // worker's FieldConfig reads key/mode/tweak via @JsonAnySetter siblings, and the platform
        // normalizer keeps a column only when `algorithm` is a non-blank string). Always present
        // so the column survives normalization (otherwise → worker 400 "At least one table
        // configuration is required"). `key` is omitted when empty so the service uses its own key.
        col.algorithm = FPE_UTF8;
        col.mode = 'encrypt';
        col.tweak = tweak;
        if (key) col.key = key;
      }
      return col;
    });
    schemasMap[schemaName].push({
      name: tableName,
      columns,
    });
  }
  return {
    datasource,
    schemas: Object.entries(schemasMap).map(([name, tables]) => ({ name, tables })),
    updatedBy,
  };
}

function cloneInventoryForDecryptMode(inventory) {
  const inv = JSON.parse(JSON.stringify(inventory && typeof inventory === 'object' ? inventory : {}));
  (inv.schemas || []).forEach((schema) => {
    (schema.tables || []).forEach((table) => {
      (table.columns || []).forEach((col, i) => {
        if (!col || !col.algorithm) return;
        // Flat column shape (current): `algorithm` is a string, `mode` a sibling. Flip mode in place.
        if (typeof col.algorithm === 'string') {
          const a = col.algorithm.toUpperCase();
          if (a === 'FPE_UTF8' || a === 'FPE_EBCDIC') {
            table.columns[i] = { ...col, mode: 'decrypt' };
          }
          return;
        }
        // Legacy nested-object shape — kept for inventories saved before the flat-shape fix.
        if (typeof col.algorithm === 'object') {
          const a = String(col.algorithm.algorithm || '').toUpperCase();
          if (a === 'FPE_UTF8' || a === 'FPE_EBCDIC') {
            table.columns[i] = { ...col, algorithm: { ...col.algorithm, mode: 'decrypt' } };
          }
        }
      });
    });
  });
  (inv.tables || []).forEach((table) => {
    const cols = table.columns;
    if (!cols || typeof cols !== 'object' || Array.isArray(cols)) return;
    Object.keys(cols).forEach((k) => {
      const def = cols[k];
      if (!def || typeof def !== 'object') return;
      if (def.algorithm && typeof def.algorithm === 'object') {
        const a = String(def.algorithm.algorithm || '').toUpperCase();
        if (a === 'FPE_UTF8' || a === 'FPE_EBCDIC') {
          cols[k] = { ...def, algorithm: { ...def.algorithm, mode: 'decrypt' } };
        }
      } else {
        const a = String(def.algorithm || '').toUpperCase();
        if (a === 'FPE_UTF8' || a === 'FPE_EBCDIC') {
          cols[k] = { ...def, mode: 'decrypt' };
        }
      }
    });
  });
  return inv;
}

function fpeFieldsListFromInventory(inventory) {
  const rows = [];
  (inventory?.schemas || []).forEach((schema) => {
    const sn = schema?.name || 'default';
    (schema.tables || []).forEach((table) => {
      const tn = table?.name;
      if (!tn) return;
      const path = sn === 'default' ? tn : `${sn}.${tn}`;
      (table.columns || []).forEach((col) => {
        if (!col || !col.sensitive || !col.name) return;
        if (isFpeColumnDef(col)) rows.push({ table: path, field: col.name });
      });
    });
  });
  (inventory?.tables || []).forEach((table) => {
    const tn = table.tableName || table.name;
    if (!tn) return;
    const cols = table.columns;
    if (cols && typeof cols === 'object' && !Array.isArray(cols)) {
      Object.entries(cols).forEach(([fieldName, def]) => {
        if (isFpeColumnDef(def)) rows.push({ table: tn, field: fieldName });
      });
    }
  });
  return rows;
}

// ---------------------------------------------------------------------------
// Inventories — a data source can have MANY named inventories, each carrying its
// own masking/encrypt provider preference. Wire shape:
//   { id, dataSourceId, name, content, preferredMaskingProviderId,
//     preferredEncryptProviderId, updatedAt }
// `content` is the inventory JSON (datasource/schemas/tables) the singular
// endpoint used to return whole.
// ---------------------------------------------------------------------------

const INVENTORIES_BASE = '/api/inventories';

/** GET /api/datasources/{id}/inventories — all inventories for a source. */
async function listDataSourceInventories(dataSourceId) {
  const res = await API.get(
    DISCOVER_RUNS_API,
    `${DATASOURCE_BASE}/${encodeURIComponent(dataSourceId)}/inventories`
  );
  return Array.isArray(res) ? res : [];
}

/**
 * POST /api/datasources/{id}/inventories — create a named inventory.
 * @param body { name, content, preferredMaskingProviderId?, preferredEncryptProviderId? }
 * @returns the created inventory (with its id)
 */
async function createDataSourceInventory(dataSourceId, body) {
  return API.post(DISCOVER_RUNS_API, `${DATASOURCE_BASE}/${encodeURIComponent(dataSourceId)}/inventories`, {
    body,
  });
}

async function getInventory(inventoryId) {
  return API.get(DISCOVER_RUNS_API, `${INVENTORIES_BASE}/${encodeURIComponent(inventoryId)}`);
}

/**
 * PUT /api/inventories/{id} — partial update: omitted fields are left unchanged;
 * a blank provider id ('') clears that preference.
 */
async function updateInventory(inventoryId, body) {
  return API.put(DISCOVER_RUNS_API, `${INVENTORIES_BASE}/${encodeURIComponent(inventoryId)}`, {
    body,
  });
}

async function deleteInventory(inventoryId) {
  return API.del(DISCOVER_RUNS_API, `${INVENTORIES_BASE}/${encodeURIComponent(inventoryId)}`);
}

/** The inventory JSON out of an inventory row; tolerates content serialized as a JSON string. */
function inventoryContentOf(inventory) {
  const c = inventory && inventory.content;
  if (c == null) return {};
  if (typeof c === 'string') {
    try {
      const parsed = JSON.parse(c);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  return typeof c === 'object' ? c : {};
}

/** Most recently updated inventory (by updatedAt, then id) — null when the source has none. */
function latestInventoryOf(inventories) {
  const list = (inventories || []).filter(Boolean);
  if (list.length === 0) return null;
  const ts = (inv) => {
    const t = inv.updatedAt ? Date.parse(inv.updatedAt) : NaN;
    return Number.isFinite(t) ? t : 0;
  };
  return list.slice().sort((a, b) => ts(b) - ts(a) || (Number(b.id) || 0) - (Number(a.id) || 0))[0];
}

/**
 * Convenience for read-only consumers (summaries, row counts) that need "the" inventory of a
 * source under the multi-inventory model: the most recently updated one, or null.
 */
async function getLatestDataSourceInventory(dataSourceId) {
  const inventories = await listDataSourceInventories(dataSourceId);
  return latestInventoryOf(inventories);
}

/**
 * GET /api/v1/masking/providers — list of enabled masking provider registrations.
 * @returns {Array<{ serviceId, name, vendor?, vendorDisplayName?, isDefault, requiresUpstreamCredentials }>}
 */
async function getMaskingProviders() {
  try {
    const res = await API.get(DISCOVER_RUNS_API, MASKING_PROVIDERS_PATH);
    return Array.isArray(res) ? res : [];
  } catch {
    return [];
  }
}

/**
 * GET /api/v1/providers?type=MASKING|ENCRYPT|DECRYPT|DISCOVER — enabled provider registrations
 * for a job family. Same entry shape as getMaskingProviders (which is now an alias for type=MASKING).
 * @param {('MASKING'|'ENCRYPT'|'DECRYPT'|'DISCOVER')} type
 * @returns {Array<{ serviceId, name, vendor?, vendorDisplayName?, isDefault, requiresUpstreamCredentials }>}
 */
async function getProviders(type) {
  try {
    const path = `${PROVIDERS_PATH}?type=${encodeURIComponent(type)}`;
    const res = await API.get(DISCOVER_RUNS_API, path);
    return Array.isArray(res) ? res : [];
  } catch {
    return [];
  }
}

/**
 * GET /api/v1/masking/algorithms/metadata — id → { description, safeHarbor, baseAlgorithm,
 * deprecated, kind, source, preservesUniqueness }. Only the C2 masking worker serves this; old
 * workers get a synthesized shape from the platform with every flag false.
 * @returns {Record<string, object>} empty on any failure — callers must treat absence as "unknown".
 */
async function getMaskingAlgorithmMetadata() {
  try {
    const res = await API.get(DISCOVER_RUNS_API, MASKING_ALGORITHMS_METADATA_PATH);
    return res && typeof res === 'object' && !Array.isArray(res) ? res : {};
  } catch {
    return {};
  }
}

/**
 * GET /api/v1/masking/algorithms — map of id → description, or legacy { algorithms: [{ id, label }] }.
 * For the C2 worker (no serviceId) each entry also carries `preservesUniqueness` from the
 * metadata endpoint, so the inventory editor's uniqueness advisory can recognise custom JARs
 * and user aliases that declare the flag (see `registerAlgorithmCatalog` in uniquenessWarning.js).
 * @param {string} [serviceId] — optional provider serviceId to scope the list.
 * @returns {Array<{ id: string, label: string, description?: string, preservesUniqueness?: boolean }>}
 */
async function listMaskingAlgorithms(serviceId) {
  const path = serviceId ? `${MASKING_ALGORITHMS_PATH}?serviceId=${encodeURIComponent(serviceId)}` : MASKING_ALGORITHMS_PATH;
  const [res, metadata] = await Promise.all([
    API.get(DISCOVER_RUNS_API, path),
    serviceId ? Promise.resolve({}) : getMaskingAlgorithmMetadata(),
  ]);
  if (!res || typeof res !== 'object') return [];
  const uniqueFlag = (id) => {
    const m = metadata[id];
    return m && typeof m === 'object' && m.preservesUniqueness === true ? { preservesUniqueness: true } : {};
  };
  const list = res.algorithms;
  if (Array.isArray(list)) {
    return list.map((a) => ({
      id: a.id,
      label: a.label || a.id,
      ...(a.description && { description: a.description }),
      ...uniqueFlag(a.id),
    }));
  }
  const skipKeys = new Set(['algorithms', 'code', 'message', 'error']);
  return Object.entries(res)
    .filter(
      ([k, v]) =>
        typeof k === 'string' &&
        !skipKeys.has(k) &&
        typeof v === 'string'
    )
    .map(([id, description]) => ({
      id,
      label: id,
      description,
      ...uniqueFlag(id),
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * GET /api/v1/masking/entity-algorithm-map — discover entity code → masking algorithm id.
 * @param {string} [serviceId] — optional provider serviceId to get that provider's map.
 * @param {string|number} [dataSourceId] — optional data source id; when it resolves to a
 *   healthcare-tagged source the platform returns the safe-harbor-overlaid map.
 * @returns {Record<string, string>}
 */
async function getMaskingEntityAlgorithmMap(serviceId, dataSourceId) {
  const query = [];
  if (serviceId) query.push(`serviceId=${encodeURIComponent(serviceId)}`);
  if (dataSourceId != null && dataSourceId !== '') {
    query.push(`dataSourceId=${encodeURIComponent(dataSourceId)}`);
  }
  const path = query.length
    ? `${MASKING_ENTITY_ALGORITHM_MAP_PATH}?${query.join('&')}`
    : MASKING_ENTITY_ALGORITHM_MAP_PATH;
  const res = await API.get(DISCOVER_RUNS_API, path);
  if (!res || typeof res !== 'object') return {};
  const skipKeys = new Set(['code', 'message', 'error', 'algorithms']);
  const out = {};
  for (const [k, v] of Object.entries(res)) {
    if (skipKeys.has(k) || typeof k !== 'string' || typeof v !== 'string') continue;
    const algo = v.trim();
    if (k && algo) out[k] = algo;
  }
  return out;
}

// Job starts (JobStartRequestDTO): MASKING/ENCRYPT/DECRYPT require `inventoryId` unless an
// inline `inventoryJson` inventory is supplied in the same request.
// Cross-source masking: pass `targetDataSourceId` to deliver masked data to a DIFFERENT
// data source — `dataSourceId` stays the source of record whose inventory is used, the
// target is what gets written. Omit it for classic in-place masking. The API pre-flights
// that the target contains the inventory's tables (422 listing the missing tables otherwise).
async function startMaskingJob({
  dataSourceId,
  inventoryId,
  inventoryJson,
  registeredServiceId,
  targetDataSourceId,
  targetDataSourceIds,
  writingStrategy,
  truncateBeforeInsert,
}) {
  const body = { dataSourceId };
  if (inventoryId != null) body.inventoryId = inventoryId;
  if (inventoryJson != null) body.inventoryJson = inventoryJson;
  if (registeredServiceId) body.registeredServiceId = registeredServiceId;
  // Fan-out: several destinations in ONE job (server maps to the worker's targetConnections[]).
  if (Array.isArray(targetDataSourceIds) && targetDataSourceIds.length > 0) {
    body.targetDataSourceIds = targetDataSourceIds;
    if (writingStrategy) body.writingStrategy = writingStrategy;
    if (truncateBeforeInsert != null) body.truncateBeforeInsert = truncateBeforeInsert;
  } else if (targetDataSourceId != null) {
    body.targetDataSourceId = targetDataSourceId;
    if (writingStrategy) body.writingStrategy = writingStrategy;
    if (truncateBeforeInsert != null) body.truncateBeforeInsert = Boolean(truncateBeforeInsert);
  }
  return API.post(DISCOVER_RUNS_API, MASKING_JOBS_BASE, { body });
}

async function listMaskingJobs({ dataSourceId, page = 0, size = 200, sort = ['id,desc'] } = {}) {
  const queryStringParameters = {
    page,
    size,
    sort,
  };
  if (dataSourceId != null) {
    queryStringParameters.dataSourceId = dataSourceId;
  }
  return API.get(DISCOVER_RUNS_API, MASKING_JOBS_BASE, { queryStringParameters });
}

/**
 * GET /api/v1/masking/jobs/{jobId} — single masking job metadata (OrchestrationJobResponseDTO):
 * dataSourceId, dataSourceName, datasourceType, engine, status, timestamps. Used by the results
 * page header (the results payload itself carries no source name/engine).
 */
async function getMaskingJob(jobId) {
  return API.get(DISCOVER_RUNS_API, `${MASKING_JOBS_BASE}/${encodeURIComponent(jobId)}`);
}

/**
 * GET /api/v1/masking/jobs/{jobId}/results — JobResultsResponseDTO { jobId, payload, status, complete }.
 * The platform API joins the submitted field→algorithm plan onto each table/file, so each unit in
 * payload.tables[] / payload.files[] carries a fields:[{ field, algorithm }] array.
 */
async function getMaskingJobResults(jobId) {
  return API.get(DISCOVER_RUNS_API, `${MASKING_JOBS_BASE}/${encodeURIComponent(jobId)}/results`);
}

/**
 * POST /api/v1/masking/jobs/target-compare — read-only fan-out preview. Compares one inventory
 * (stored `inventoryId` or inline `inventoryJson`) against each candidate target and returns
 * `{ reports: [TargetComparisonReport], fanoutPlan: { sharedTargets, splitTargets, pendingTargets,
 * excludedTargets } }`. A target with no fresh Profile gets one started server-side and comes
 * back `profilePending` with its `profileJobId`; compare again once that job completes.
 */
async function compareInventoryTargets({ dataSourceId, inventoryId, inventoryJson, targetDataSourceIds }) {
  const body = { dataSourceId, targetDataSourceIds };
  if (inventoryId != null) body.inventoryId = inventoryId;
  if (inventoryJson != null) body.inventoryJson = inventoryJson;
  return API.post(DISCOVER_RUNS_API, `${MASKING_JOBS_BASE}/target-compare`, { body });
}

/**
 * POST /api/inventories/{id}/derive-for-target — compare the inventory's ROOT against one target
 * and create / regenerate the `target_<name>` inventory trimmed to what the target has. Returns
 * `{ report, inventory | null, created }`; `inventory` is null when no trimmed copy was needed
 * (no gaps) or none could be made (profile pending, no snapshot, nothing left to mask).
 */
async function deriveInventoryForTarget(inventoryId, targetDataSourceId) {
  return API.post(DISCOVER_RUNS_API, `${INVENTORIES_BASE}/${encodeURIComponent(inventoryId)}/derive-for-target`, {
    body: { targetDataSourceId },
  });
}

/** GET /api/v1/profile/jobs/{jobId} — used to wait out a target's Profile before re-comparing. */
async function getProfileJob(jobId) {
  return API.get(DISCOVER_RUNS_API, `/api/v1/profile/jobs/${encodeURIComponent(jobId)}`);
}

/** True for a target-derived (`target_<name>`) inventory row from the inventories API. */
function isDerivedInventory(inv) {
  return Boolean(inv && inv.derivedFromInventoryId != null && inv.targetDataSourceId != null);
}

async function listEncryptJobs({ dataSourceId, page = 0, size = 500, sort = ['id,desc'] } = {}) {
  const queryStringParameters = { page, size, sort };
  if (dataSourceId != null) queryStringParameters.dataSourceId = dataSourceId;
  return API.get(DISCOVER_RUNS_API, ENCRYPT_JOBS_BASE, { queryStringParameters });
}

async function listDecryptJobs({ dataSourceId, page = 0, size = 500, sort = ['id,desc'] } = {}) {
  const queryStringParameters = { page, size, sort };
  if (dataSourceId != null) queryStringParameters.dataSourceId = dataSourceId;
  return API.get(DISCOVER_RUNS_API, DECRYPT_JOBS_BASE, { queryStringParameters });
}

// Encrypt/decrypt job metadata + results — same endpoints/DTO shape as masking
// (OrchestrationJobResponseDTO / JobResultsResponseDTO). The shared JobResults page consumes these.
async function getEncryptJob(jobId) {
  return API.get(DISCOVER_RUNS_API, `${ENCRYPT_JOBS_BASE}/${encodeURIComponent(jobId)}`);
}

async function getEncryptJobResults(jobId) {
  return API.get(DISCOVER_RUNS_API, `${ENCRYPT_JOBS_BASE}/${encodeURIComponent(jobId)}/results`);
}

async function getDecryptJob(jobId) {
  return API.get(DISCOVER_RUNS_API, `${DECRYPT_JOBS_BASE}/${encodeURIComponent(jobId)}`);
}

async function getDecryptJobResults(jobId) {
  return API.get(DISCOVER_RUNS_API, `${DECRYPT_JOBS_BASE}/${encodeURIComponent(jobId)}/results`);
}

/**
 * Live progress fields for a running masking/encrypt/decrypt job, from the job DTO (and its
 * cachedStatusJson worker snapshot). Job-type-agnostic — all three carry the same
 * workItemsTotal/Completed + recordsTotal/Processed fields. Returns null when no progress is known.
 */
function jobProgressFromJob(job) {
  if (!job) return null;
  let cached = {};
  const raw = job.cachedStatusJson;
  if (raw != null && raw !== '') {
    try {
      cached = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
      cached = {};
    }
  }
  const firstNum = (...vals) => {
    for (const v of vals) {
      if (v == null || v === '') continue;
      const n = Number(v);
      if (!Number.isNaN(n) && n >= 0) return n;
    }
    return null;
  };
  const tablesTotal = firstNum(job.workItemsTotal, cached.workItemsTotal, job.totalTables, cached.totalTables);
  const tablesCompleted = firstNum(
    job.workItemsCompleted, cached.workItemsCompleted, job.tablesCompleted, cached.tablesCompleted
  );
  const recordsTotal = firstNum(job.recordsTotal, cached.recordsTotal, cached.totalRowsToProcess);
  const recordsCompleted = firstNum(job.recordsProcessed, cached.recordsProcessed, cached.rowsProcessed);
  const currentTableName =
    job.currentTableName || job.currentTable || cached.currentTableName || cached.currentTable || null;
  const hasTableProgress = tablesTotal != null && tablesTotal > 0;
  if (!hasTableProgress && recordsTotal == null && recordsCompleted == null && !currentTableName) {
    return null;
  }
  return {
    tablesCompleted: hasTableProgress ? Math.min(tablesCompleted ?? 0, tablesTotal) : tablesCompleted,
    tablesTotal: hasTableProgress ? tablesTotal : null,
    recordsCompleted,
    recordsTotal,
    currentTableName:
      currentTableName != null && String(currentTableName).trim() !== '' ? String(currentTableName).trim() : null,
  };
}

async function startEncryptJob({ dataSourceId, inventoryId, inventoryJson, registeredServiceId }) {
  const body = { dataSourceId };
  if (inventoryId != null) body.inventoryId = inventoryId;
  if (inventoryJson != null) body.inventoryJson = inventoryJson;
  if (registeredServiceId) body.registeredServiceId = registeredServiceId;
  return API.post(DISCOVER_RUNS_API, ENCRYPT_JOBS_BASE, { body });
}

async function startDecryptJob({ dataSourceId, inventoryId, inventoryJson, registeredServiceId }) {
  const body = { dataSourceId };
  if (inventoryId != null) body.inventoryId = inventoryId;
  if (inventoryJson != null) body.inventoryJson = inventoryJson;
  if (registeredServiceId) body.registeredServiceId = registeredServiceId;
  return API.post(DISCOVER_RUNS_API, DECRYPT_JOBS_BASE, { body });
}

/**
 * Decrypt using a saved inventory. When no explicit inventoryId is given, picks the source's
 * inventory that actually carries FPE columns (most recently updated wins). The content is sent
 * INLINE with every FPE column's mode flipped to 'decrypt' — the stored inventory is the encrypt
 * plan, so starting by bare inventoryId would re-encrypt. The inventory's encrypt provider is
 * forwarded so decryption runs on the same service that encrypted; `registeredServiceId`
 * overrides it (the landscape re-run button passes the original job's provider).
 */
async function startDecryptJobWithSavedInventory(dataSourceId, inventoryId, registeredServiceId) {
  let inventory = null;
  if (inventoryId != null) {
    inventory = await getInventory(inventoryId);
  } else {
    const inventories = await listDataSourceInventories(dataSourceId);
    const withFpe = inventories.filter(
      (inv) => fpeFieldsListFromInventory(inventoryContentOf(inv)).length > 0
    );
    inventory = latestInventoryOf(withFpe.length > 0 ? withFpe : inventories);
  }
  const content = inventoryContentOf(inventory);
  if (!inventory || Object.keys(content).length === 0) {
    throw new Error('No saved inventory for this data source');
  }
  const invDecrypt = cloneInventoryForDecryptMode(content);
  return startDecryptJob({
    dataSourceId,
    inventoryJson: JSON.stringify(invDecrypt),
    registeredServiceId: registeredServiceId || inventory.preferredEncryptProviderId || undefined,
  });
}

/**
 * The inventory id to re-run with: the job's recorded one — VERIFIED to still exist, since
 * inventories can be deleted after the job ran (the recorded id then 404s at start with
 * "inventory not found") — else the source's most recently updated inventory (also the
 * fallback for pre-column jobs that recorded none).
 */
async function resolveRerunInventoryId(dataSourceId, inventoryId) {
  if (inventoryId != null) {
    try {
      const existing = await getInventory(inventoryId);
      if (existing && existing.id != null && existing.code !== 'INVENTORY_NOT_FOUND') {
        return inventoryId;
      }
    } catch (e) {
      // Deleted since the job ran — fall through to the source's latest.
    }
  }
  const inventory = await getLatestDataSourceInventory(dataSourceId);
  if (!inventory) {
    throw new Error('No saved inventory for this data source');
  }
  return inventory.id;
}

/**
 * One-click re-run for a landscape Job Results row: same operation and same provider (the job's
 * recorded registeredServiceId), against the inventory the job actually ran with. `inventoryId`
 * comes off the job record; jobs created before that column existed pass null and fall back to
 * the source's most recently updated inventory. Decryption re-runs through the mode-flipping
 * saved-inventory path.
 */
async function rerunJobForRow({
  dataSourceId, operation, registeredServiceId, inventoryId, targetDataSourceId, targetDataSourceIds,
}) {
  const invId = await resolveRerunInventoryId(dataSourceId, inventoryId);
  if (operation === 'Decryption') {
    return startDecryptJobWithSavedInventory(dataSourceId, invId, registeredServiceId || undefined);
  }
  const start = operation === 'Encryption' ? startEncryptJob : startMaskingJob;
  // Cross-source rows re-run against the SAME destination(s) — dropping them would silently
  // turn the re-run into an in-place mask of the source (403 for read-only sources of record).
  // A fan-out row re-runs as one multi-target job.
  const targets =
    Array.isArray(targetDataSourceIds) && targetDataSourceIds.length > 0
      ? targetDataSourceIds
      : targetDataSourceId != null
        ? [targetDataSourceId]
        : [];
  return start({
    dataSourceId,
    inventoryId: invId,
    registeredServiceId: registeredServiceId || undefined,
    ...(operation === 'Masking' && targets.length === 1 ? { targetDataSourceId: targets[0] } : {}),
    ...(operation === 'Masking' && targets.length > 1 ? { targetDataSourceIds: targets } : {}),
  });
}

// Masking job groups — ordered sets of masking jobs run serially as one unit
// (POST /api/v1/masking/job-groups/... on the platform API). Every group read embeds its
// steps and latest run (with per-step status), so one list call renders the Orchestration Groups page.

async function listMaskingJobGroups() {
  return API.get(DISCOVER_RUNS_API, MASKING_JOB_GROUPS_BASE);
}

async function getMaskingJobGroup(groupId) {
  return API.get(DISCOVER_RUNS_API, `${MASKING_JOB_GROUPS_BASE}/${encodeURIComponent(groupId)}`);
}

async function createMaskingJobGroup({ name, description, steps }) {
  const body = { name };
  if (description != null) body.description = description;
  if (Array.isArray(steps) && steps.length) body.steps = steps;
  return API.post(DISCOVER_RUNS_API, MASKING_JOB_GROUPS_BASE, { body });
}

async function updateMaskingJobGroup(groupId, { name, description }) {
  return API.put(DISCOVER_RUNS_API, `${MASKING_JOB_GROUPS_BASE}/${encodeURIComponent(groupId)}`, {
    body: { name, description },
  });
}

/**
 * Full ordered replacement of the step list — one call covers add, remove, and reorder.
 * steps: [{ dataSourceId, inventoryId, targetDataSourceId? }] in run order.
 */
async function replaceMaskingJobGroupSteps(groupId, steps) {
  return API.put(DISCOVER_RUNS_API, `${MASKING_JOB_GROUPS_BASE}/${encodeURIComponent(groupId)}/steps`, {
    body: { steps },
  });
}

async function deleteMaskingJobGroup(groupId) {
  return API.del(DISCOVER_RUNS_API, `${MASKING_JOB_GROUPS_BASE}/${encodeURIComponent(groupId)}`);
}

async function runMaskingJobGroup(groupId) {
  return API.post(DISCOVER_RUNS_API, `${MASKING_JOB_GROUPS_BASE}/${encodeURIComponent(groupId)}/run`, {});
}

async function cancelMaskingJobGroupRun(groupId) {
  return API.post(DISCOVER_RUNS_API, `${MASKING_JOB_GROUPS_BASE}/${encodeURIComponent(groupId)}/cancel`, {});
}

// Cooperative cancel of an in-flight secure job. Each family has its own real backend endpoint
// (POST /api/v1/{masking|encrypt|decrypt}/jobs/{jobId}/cancel) delegating to the same
// OrchestrationJobService.cancelJob with the right type. (The old discoverApi `cancelMaskingJob`
// alias posted to the DISCOVER endpoint and is deprecated — do not use it for masking.)
function cancelMaskingJobById(jobId) {
  if (jobId == null) throw new Error('Missing job id for masking cancel.');
  return API.post(DISCOVER_RUNS_API, `${MASKING_JOBS_BASE}/${encodeURIComponent(jobId)}/cancel`, {});
}
function cancelEncryptJob(jobId) {
  if (jobId == null) throw new Error('Missing job id for encrypt cancel.');
  return API.post(DISCOVER_RUNS_API, `${ENCRYPT_JOBS_BASE}/${encodeURIComponent(jobId)}/cancel`, {});
}
function cancelDecryptJob(jobId) {
  if (jobId == null) throw new Error('Missing job id for decrypt cancel.');
  return API.post(DISCOVER_RUNS_API, `${DECRYPT_JOBS_BASE}/${encodeURIComponent(jobId)}/cancel`, {});
}

export {
  jobTargetIdsOf,
  mutatedDataSourceIdsOf,
  cancelMaskingJobById,
  cancelEncryptJob,
  cancelDecryptJob,
  deriveMaskStatusByDataSource,
  cloneInventoryForDecryptMode,
  fpeFieldsListFromInventory,
  getSensitiveFields,
  listDataSourceInventories,
  createDataSourceInventory,
  getInventory,
  updateInventory,
  deleteInventory,
  inventoryContentOf,
  latestInventoryOf,
  getLatestDataSourceInventory,
  getSensitiveFieldCountFromInventory,
  getMaskedInventorySummary,
  transformMaskingJobsToReportRows,
  transformEncryptionJobsToReportRows,
  readableJobError,
  countSourcesWithLatestFailure,
  secureEligibleConnections,
  inventoryToSelectedFields,
  getMaskingEntityAlgorithmMap,
  getMaskingProviders,
  getProviders,
  listMaskingAlgorithms,
  getMaskingAlgorithmMetadata,
  listMaskingJobs,
  getMaskingJob,
  getMaskingJobResults,
  compareInventoryTargets,
  deriveInventoryForTarget,
  getProfileJob,
  isDerivedInventory,
  listMaskingJobGroups,
  getMaskingJobGroup,
  createMaskingJobGroup,
  updateMaskingJobGroup,
  replaceMaskingJobGroupSteps,
  deleteMaskingJobGroup,
  runMaskingJobGroup,
  cancelMaskingJobGroupRun,
  listEncryptJobs,
  listDecryptJobs,
  getEncryptJob,
  getEncryptJobResults,
  getDecryptJob,
  getDecryptJobResults,
  jobProgressFromJob,
  mapEncryptionRowStatus,
  mergeEncryptionJobsForDataSource,
  selectedFieldsToInventory,
  selectedFieldsToEncryptInventory,
  startMaskingJob,
  startEncryptJob,
  startDecryptJob,
  startDecryptJobWithSavedInventory,
  rerunJobForRow,
  getSecureDataLandscape,
};